import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { Eye, EyeOff, X } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { authApi } from '../api/auth';
import { logger } from '../utils/logger';
import { isEmptyChurchId } from '../utils/churchId';
import { setVerificationData } from '../utils/sessionStorage';
import { useTheme } from '../theme/theme';
import type { VerificationSessionData } from '../types';
import { AuthCard, AuthScreen, AuthTitle } from '../components/auth/AuthScreen';
import { Banner, Button, Field, Input, Segmented } from '../components/ui';

type Mode = 'login' | 'signup';
type FieldErrors = Partial<Record<'fullName' | 'email' | 'password', string>>;

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, setUser } = useAuth();

  // After sign-in, return the user to where they deep-linked (e.g. /upload).
  // Sanitized to internal paths only to avoid an open redirect.
  const params = new URLSearchParams(location.search);
  const rawRedirect = params.get('redirect');
  const redirectTo =
    rawRedirect && rawRedirect.startsWith('/') && !rawRedirect.startsWith('//') ? rawRedirect : null;
  const isUploadContext = redirectTo?.startsWith('/upload') ?? false;
  const resetSucceeded = params.get('reset') === 'success';

  const [mode, setMode] = useState<Mode>('login');
  const isLogin = mode === 'login';
  const [formData, setFormData] = useState({ email: '', password: '', fullName: '' });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [showCreateChurchOption, setShowCreateChurchOption] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [logoutReason, setLogoutReason] = useState<string | null>(null);
  const [shake, setShake] = useState(0);

  // The member Song Studio sign-in uses the /upload theme (dark by default);
  // the admin sign-in follows the admin preference.
  const { isDark } = useTheme(isUploadContext ? 'member' : 'admin');

  const handleGoogleSuccess = async (credentialResponse: any) => {
    try {
      setLoading(true);
      setError(null);
      logger.info('Google OAuth: ID token received from credential response');

      // credentialResponse.credential contains the ID token (JWT)
      // Song Studio (/upload) is for ANY church member: use the member context
      // so the backend doesn't reject non-admin users (admin_portal is
      // admin-only and returns 401 needsChurchCreation for members).
      const response = await authApi.loginWithGoogle(
        credentialResponse.credential,
        isUploadContext ? 'member_portal' : undefined,
      );

      // loginWithGoogle already saved tokens — just set user state directly
      // (Don't call login() which would make a redundant email/password API call)
      const userData = {
        id: response.id,
        username: response.name,
        email: response.email,
        userRole: response.userRole,
        churchId: response.churchId,
        userLogo: response.userLogo,
        adminChurches: response.adminChurches,
      };
      localStorage.setItem('auth_user', JSON.stringify(userData));
      setUser(userData);

      logger.info('Google OAuth: Login successful');

      // Multi-church admin: show church selector (keep the return-to target
      // alive so picking a church doesn't strand them on the dashboard)
      if (response.adminChurches && response.adminChurches.length > 1) {
        navigate(redirectTo ? `/select-church?redirect=${encodeURIComponent(redirectTo)}` : '/select-church');
        return;
      }

      if (redirectTo) {
        navigate(redirectTo);
        return;
      }
      navigate(isEmptyChurchId(response.churchId) ? '/admin/church-setup' : '/admin/dashboard');
    } catch (error: any) {
      // Check if email verification is required
      if (error.response?.status === 403 && error.response?.data?.error === 'REQUIRES_VERIFICATION') {
        logger.info('Google OAuth: Email verification required');
        const { email, sessionToken, userId } = error.response.data;

        // Store verification data in session storage
        const verificationData: VerificationSessionData = {
          email,
          sessionToken,
          userId,
          timestamp: Date.now(),
        };
        setVerificationData(verificationData);

        // Redirect to verification page
        navigate('/verify-email');
        return;
      }

      // Handle needsChurchCreation (401 - user exists but no admin role)
      const resData = { ...error.response?.data, ...(error.response?.data?.errors || {}) };
      if (error.response?.status === 401 && resData?.needsChurchCreation) {
        logger.info('Google OAuth: Not an admin; retrying as member for Song Studio');
        // Not an admin: sign them in as a member instead and send them to the
        // member Song Studio. The /upload page handles the no-church case with
        // a create/join prompt + a link to the admin guide on churchnow.co.
        try {
          const memberResponse = await authApi.loginWithGoogle(credentialResponse.credential, 'member_portal');
          const memberData = {
            id: memberResponse.id,
            username: memberResponse.name,
            email: memberResponse.email,
            userRole: memberResponse.userRole,
            churchId: memberResponse.churchId,
            userLogo: memberResponse.userLogo,
            adminChurches: memberResponse.adminChurches,
          };
          localStorage.setItem('auth_user', JSON.stringify(memberData));
          setUser(memberData);
          navigate(redirectTo ?? '/upload');
          return;
        } catch (memberError) {
          logger.error('Google OAuth: Member fallback login failed', memberError);
        }

        // Fallback: the original admin onboarding funnel.
        const memberships = resData.existingMemberships || [];
        if (memberships.length > 0) {
          const churchNames = memberships.map((m: any) => m.churchName || 'Unknown Church').join(', ');
          setError(
            `You're currently a member of: ${churchNames}. You can ask the admin of that church to add you as an admin, or proceed to create a new church.`,
          );
          setShowCreateChurchOption(true);
          // Store userId so they can proceed to church creation if they click the link
          if (resData.userId) {
            localStorage.setItem('pending_admin_userId', String(resData.userId));
          }
        } else {
          if (resData.userId) {
            localStorage.setItem('pending_admin_userId', String(resData.userId));
          }
          navigate('/create-church');
        }
        return;
      }

      logger.error('Google OAuth: Login failed', error);
      setError(error.response?.data?.message || error.response?.data?.error || 'Google login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setLoading(false);
    logger.error('Google OAuth: Error during authentication');
    setError('Google login was cancelled or failed. Please try again.');
  };

  // Check for logout reason on mount
  useEffect(() => {
    const reason = localStorage.getItem('logout_reason');
    if (reason) {
      setLogoutReason(reason);
      localStorage.removeItem('logout_reason');
    }
  }, []);

  const validate = (): FieldErrors => {
    const er: FieldErrors = {};
    if (!isLogin && !formData.fullName.trim()) er.fullName = 'Enter your name.';
    if (!/^\S+@\S+\.\S+$/.test(formData.email)) er.email = 'Enter a valid email address.';
    if (!formData.password) er.password = 'Enter your password.';
    else if (!isLogin) {
      if (formData.password.length < 8) er.password = 'Use at least 8 characters.';
      else if (!/[A-Za-z]/.test(formData.password)) er.password = 'Password must contain at least one letter.';
      else if (!/\d/.test(formData.password)) er.password = 'Password must contain at least one number.';
    }
    return er;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const er = validate();
    setFieldErrors(er);
    if (Object.keys(er).length) {
      setShake((n) => n + 1);
      return;
    }

    setLoading(true);

    try {
      if (isLogin) {
        // Login flow. Song Studio (/upload) accepts ANY church member, so use
        // the member context there (admin_portal rejects non-admins with a 401).
        const result = await login(
          { email: formData.email, password: formData.password },
          isUploadContext ? 'member_portal' : undefined,
        );

        // Multi-church admin: show church selector (carry the return-to target)
        if (result.adminChurches && result.adminChurches.length > 1) {
          navigate(redirectTo ? `/select-church?redirect=${encodeURIComponent(redirectTo)}` : '/select-church');
          return;
        }

        if (redirectTo) {
          navigate(redirectTo);
          return;
        }
        const storedUser = JSON.parse(localStorage.getItem('auth_user') || '{}');
        navigate(isEmptyChurchId(storedUser.churchId) ? '/admin/church-setup' : '/admin/dashboard');
      } else {
        // Registration flow. The form no longer asks for the password twice;
        // the register DTO still carries confirmPassword, so send it matching.
        const response = await authApi.register({
          username: formData.fullName,
          email: formData.email,
          password: formData.password,
          confirmPassword: formData.password,
          userRole: 'Admin',
        });

        // If we get here with a 200/201 response, check for sessionToken
        if (response?.data?.sessionToken) {
          const verificationData: VerificationSessionData = {
            email: formData.email,
            sessionToken: response.data.sessionToken,
          };
          setVerificationData(verificationData);
          window.location.href = '/verify-email';
          return;
        }

        // Should not reach here
        const storedReg = JSON.parse(localStorage.getItem('auth_user') || '{}');
        navigate(isEmptyChurchId(storedReg.churchId) ? '/admin/church-setup' : '/admin/dashboard');
      }
    } catch (error: unknown) {
      console.error('🔴 Registration error:', error);

      // Type guard for Axios errors
      const isAxiosError = (err: unknown): err is { response?: { status?: number; data?: any } } => {
        return typeof err === 'object' && err !== null && 'response' in err;
      };

      // Type guard for custom errors
      const hasCode = (err: unknown): err is { code: string; sessionToken?: string } => {
        return typeof err === 'object' && err !== null && 'code' in err;
      };

      // Helper to read data from ApiResponse wrapper (data may be in .errors or at top level)
      const getResponseData = (data: any) => ({
        ...data,
        ...(data?.errors || {}),
        ...(data?.data || {}),
      });

      if (isAxiosError(error)) {
        const resData = getResponseData(error.response?.data);

        // Handle needsChurchCreation (401 response for verified users without admin role)
        if (error.response?.status === 401 && resData?.needsChurchCreation) {
          logger.info('Not an admin; retrying as member for Song Studio');
          // Not an admin: sign them in as a member instead and send them to the
          // member Song Studio. The /upload page handles the no-church case
          // with a create/join prompt + the admin guide on churchnow.co.
          if (isLogin) {
            try {
              await login({ email: formData.email, password: formData.password }, 'member_portal');
              navigate(redirectTo ?? '/upload');
              return;
            } catch (memberError) {
              logger.error('Member fallback login failed', memberError);
            }
          }

          // Fallback: the original admin onboarding funnel.
          if (resData.userId) {
            localStorage.setItem('pending_admin_userId', String(resData.userId));
          }
          navigate('/create-church');
          return;
        }

        // Handle existing account (400 response during registration)
        if (error.response?.status === 400 && resData?.existingAccount) {
          const provider = resData.provider;
          if (provider === 'Google') {
            setError('An account with this email already exists. Please sign in with Google instead.');
          } else {
            setError('An account with this email already exists. Please sign in instead.');
          }
          return;
        }

        // Handle verification required (403 response)
        if (error.response?.status === 403 && (resData?.sessionToken || error.response?.data?.sessionToken)) {
          const sessionToken = resData?.sessionToken || error.response?.data?.sessionToken;
          const verificationData: VerificationSessionData = {
            email: formData.email,
            userId: resData?.userId || '',
            sessionToken,
          };
          setVerificationData(verificationData);

          window.location.href = '/verify-email';
          return;
        }

        // Generic API error
        const errorMessage =
          resData?.message ||
          error.response?.data?.error ||
          (isLogin ? 'Login failed. Please check your credentials.' : 'Registration failed. Please try again.');
        setError(errorMessage);
      } else if (hasCode(error) && error.code === 'REQUIRES_VERIFICATION' && error.sessionToken) {
        logger.info('Verification required, redirecting to verify-email');

        const verificationData: VerificationSessionData = {
          email: formData.email,
          userId: '',
          sessionToken: error.sessionToken,
        };
        setVerificationData(verificationData);

        window.location.href = '/verify-email';
        return;
      } else {
        // Non-Axios error
        const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred.';
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (next: Mode) => {
    if (next === mode) return;
    setMode(next);
    setError(null);
    setFieldErrors({});
    setShowCreateChurchOption(false);
    setFormData({ email: '', password: '', fullName: '' });
    setShowPassword(false);
  };

  const set = (k: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [k]: e.target.value });
    if (fieldErrors[k]) setFieldErrors({ ...fieldErrors, [k]: undefined });
  };

  const title = isLogin ? 'Welcome back' : 'Create your account';
  const sub = isUploadContext
    ? 'Upload rehearsal recordings from your computer and get the chords and key for your worship team.'
    : isLogin
      ? 'Log in to Worship Planner'
      : 'Plan services, songs and rosters with your team';

  return (
    <AuthScreen themeScope={isUploadContext ? 'member' : 'admin'}>
      <AuthTitle eyebrow={isUploadContext ? 'SONG STUDIO' : undefined} title={title} sub={sub} />

      {(logoutReason || resetSucceeded) && (
        <div className="w-full mb-3.5 flex items-start gap-3" style={{ animation: 'wpRise .6s var(--ease) both' }}>
          {logoutReason ? (
            <Banner tone="amb" className="flex-1 flex items-start gap-2" role="status">
              <span className="flex-1">{logoutReason}</span>
              <button type="button" aria-label="Dismiss" onClick={() => setLogoutReason(null)} className="shrink-0">
                <X size={16} aria-hidden="true" />
              </button>
            </Banner>
          ) : (
            <Banner tone="ok" className="flex-1" role="status">
              Password updated. Log in with your new password.
            </Banner>
          )}
        </div>
      )}

      <AuthCard as="form" onSubmit={handleSubmit} noValidate shake={shake} className="p-2 pb-[26px]">
        <Segmented
          label="Account"
          size="lg"
          block
          className="mb-6"
          value={mode}
          onChange={switchMode}
          options={[
            { value: 'login', label: 'Log in' },
            { value: 'signup', label: 'Sign up' },
          ]}
        />
        <div className="px-[18px] flex flex-col gap-4">
          {error && (
            <Banner tone="acc" role="alert">
              <p>{error}</p>
              {showCreateChurchOption && (
                <button type="button" onClick={() => navigate('/create-church')} className="mt-2 font-medium underline text-t1">
                  Proceed to create a new church →
                </button>
              )}
            </Banner>
          )}

          {!isLogin && (
            <div className="wp-fade">
              <Field label="Full name" htmlFor="auth-name" error={fieldErrors.fullName}>
                <Input
                  id="auth-name"
                  height={48}
                  value={formData.fullName}
                  onChange={set('fullName')}
                  placeholder="Michael Reyes"
                  autoComplete="name"
                  invalid={!!fieldErrors.fullName}
                  className="!text-[15px]"
                />
              </Field>
            </div>
          )}

          <Field label="Email" htmlFor="auth-email" error={fieldErrors.email}>
            <Input
              id="auth-email"
              type="email"
              height={48}
              value={formData.email}
              onChange={set('email')}
              placeholder="you@church.org"
              autoComplete="email"
              invalid={!!fieldErrors.email}
              className="!text-[15px]"
            />
          </Field>

          <Field
            label="Password"
            htmlFor="auth-password"
            error={fieldErrors.password}
            labelAside={
              isLogin ? (
                <button type="button" onClick={() => navigate('/forgot-password')} className="text-[12.5px] text-link hover:text-acc">
                  Forgot password?
                </button>
              ) : undefined
            }
          >
            <Input
              id="auth-password"
              type={showPassword ? 'text' : 'password'}
              height={48}
              value={formData.password}
              onChange={set('password')}
              placeholder={isLogin ? 'Your password' : 'At least 8 characters'}
              autoComplete={isLogin ? 'current-password' : 'new-password'}
              invalid={!!fieldErrors.password}
              className="!text-[15px]"
              trailing={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  className="w-9 h-9 rounded-[9px] flex items-center justify-center text-t4 hover:bg-fill2 hover:text-t1"
                >
                  {showPassword ? <EyeOff size={18} strokeWidth={1.7} aria-hidden="true" /> : <Eye size={18} strokeWidth={1.7} aria-hidden="true" />}
                </button>
              }
            />
          </Field>

          <Button type="submit" size="xl" shape="form" glow disabled={loading} block className="text-[15px]">
            {loading && <span className="wp-spinner w-4 h-4" style={{ borderColor: 'rgba(255,255,255,.35)', borderTopColor: '#fff' }} aria-hidden="true" />}
            {loading ? (isLogin ? 'Logging in…' : 'Creating account…') : isLogin ? 'Log in' : 'Create account'}
          </Button>

          <div className="flex items-center gap-3 text-[12px] text-t5" aria-hidden="true">
            <span className="flex-1 h-px bg-line2" />
            or
            <span className="flex-1 h-px bg-line2" />
          </div>

          {/* Google's own button (Sign in with Google renders it in an iframe);
              styled through its props so it matches the ghost pill. */}
          <div className="flex justify-center min-h-[44px]">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              useOneTap={false}
              type="standard"
              theme={isDark ? 'filled_black' : 'outline'}
              size="large"
              text="continue_with"
              shape="pill"
              logo_alignment="center"
              width="340"
            />
          </div>

          {!isLogin && (
            <p className="text-[12.5px] leading-[1.55] text-t4 text-center">
              By creating an account you agree to the Terms and Privacy Policy.
            </p>
          )}
        </div>
      </AuthCard>

      <div className="text-[13.5px] text-t4 mt-6 flex flex-wrap justify-center gap-1.5" style={{ animation: 'wpRise .8s .3s var(--ease) both' }}>
        {isUploadContext ? (
          <>
            <span>Managing a church?</span>
            <Link to="/login">Go to the admin app</Link>
          </>
        ) : (
          <>
            <span>{isLogin ? 'New to Worship Planner?' : 'Already have an account?'}</span>
            <button type="button" onClick={() => switchMode(isLogin ? 'signup' : 'login')} className="text-link hover:text-acc">
              {isLogin ? 'Create an account' : 'Log in'}
            </button>
          </>
        )}
      </div>
    </AuthScreen>
  );
};
